import { Inject } from '@odinjs/odin';

import { ContextualBarAPI, FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import moment from 'moment';

import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { EnteralNutritionTerminateAliases, FragmentNames } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import MedicationService from '../../../services/medication-service';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

interface GenerateChangePrescribedSolutionResponse {
  messageDescription: string;
}

@Controller({ component: EnteralNutritionTerminateAliases.TERMINATE_ENTERAL_NUTRITION, fragment: FragmentNames.ENTERAL_NUTRITION_TERMINATE })
export class EnteralNutritionTerminateController extends BaseController {

  @Inject(EnteralNutritionTerminateAliases.TERMINATE_ENTERAL_NUTRITION_FORM)
  private interruptFormAPI!: FormAPI;

  @Inject(EnteralNutritionTerminateAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(EnteralNutritionTerminateAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(EnteralNutritionTerminateAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: InputAPI;

  @Inject(EnteralNutritionTerminateAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @OnMount(EnteralNutritionTerminateAliases.CONTEXTUAL_BAR_FORM)
  handleMountContextualBarForm(event: MountEvent): void {
    const materialDescription = MedicineAndSolutionsParams.params.dsMaterialId as string
    const contextualBarApi = event.target as ContextualBarAPI
    contextualBarApi.setTitle(materialDescription)
  }

  @OnMount(EnteralNutritionTerminateAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const api = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    api.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeSubmit(EnteralNutritionTerminateAliases.TERMINATE_ENTERAL_NUTRITION_FORM)
  handleBeforeSubmit(): void {
    const body = this.createRequestBody()
    this.handleTerminateEnteralNutritionRequest(body);
  }

  @OnBeforeCancel(EnteralNutritionTerminateAliases.TERMINATE_ENTERAL_NUTRITION_FORM)
  onFormCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(EnteralNutritionTerminateAliases.TERMINATE_ENTERAL_NUTRITION)
  handleLayoutInterruptMount(): void {
    const discardedVolume = 0;
    this.interruptFormAPI.setData({
      ...MedicineAndSolutionsParams.params,
      discardedVolume,
      infusedVolume: this.getInfusedVolume()
    });
  }

  private getInfusedVolume(): number {
    const defaultValue = 0
    const infusedVolume = Number(MedicineAndSolutionsParams.params.qtDoseId || 0) - Number(MedicineAndSolutionsParams.params.partialVolume || 0)
    return Math.max(defaultValue, parseFloat(infusedVolume.toFixed(2)))
  }

  @OnChange(EnteralNutritionTerminateAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  @OnChange(EnteralNutritionTerminateAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  private createRequestBody(): Record<string, Value> {
    const { encounterId, prescriptionNumber, startDate, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const { currentTime } = this.context.getBulkData() as { currentTime: string };

    const formValues = this.interruptFormAPI.getValue()
    const body = {
      dateChange: currentTime,
      dateHour: moment(startDate as string).format(ISO_FULL_DATE_FORMAT),
      discardedVolumeQuantity: this.discardedVolumeInputAPI.getValue() as number ?? 0,
      encounterNumber: Number(encounterId),
      infusedVolumeQuantity: formValues.infusedVolume as number ?? 0,
      infusionSpeedQuantity: Number(formValues.infusionSpeed) ?? 0,
      observationDescription: this.notesJustificationTextAreaAPI.getValue() as string,
      partialVolumeQuantity: formValues.partialVolume as number,
      prescriptionNumber: Number(prescriptionNumber),
      solutionNumber: Number(sequenceNumber),
      typeDosage: '',
      volumeSafeQuantity: formValues.infusedVolume as number,
    }

    return body;
  }

  private handleTerminateEnteralNutritionRequest(body: Record<string, Value>): Promise<void> {
    this.interruptFormAPI.setLoading(true)
    this.interruptFormAPI.setEditing(false)
    return HttpService.post(
      Endpoint.EMAR_TERMINATE_ENTERAL_NUTRITION,
      {},
      { body }
    ).then(response => {
      const responseBody = response.body as GenerateChangePrescribedSolutionResponse;
      if (responseBody) {
        this.showResponseMessage(responseBody?.messageDescription);
        this.redirectToTimeline();
      }
    }).catch(error => {
      if (error.body && error.body.errors) {
        error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
      }
    }).finally(() => {
      this.interruptFormAPI.setEditing(true)
      this.interruptFormAPI.setLoading(false)
    });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('label.infusion-viewer.toast-infusion-terminated'), ToastLevel.SUCCESS);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if(this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }
    const doseQuantity = Number(MedicineAndSolutionsParams.params.qtDoseId);
    const partialVolume = this.partialVolumeInputAPI.getValue() as number;
    const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, doseQuantity);
    input.setValue(updatedVolume);
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }
}